/**
 * PartnerCompanySelect Component (T041 - US2)
 * Searchable dropdown for selecting a partner company
 *
 * Features:
 * - Searchable dropdown (Radix Combobox pattern)
 * - Debounced search (300ms)
 * - Shows company name + referral code
 * - Clear button to remove assignment
 * - Loading state during search
 */

import { useState, useRef, useEffect } from 'react';
import { Search, X, Building2, ChevronDown, Check } from 'lucide-react';
import { usePartnerCompanySelect } from '../hooks/usePartnerCompanySelect';
import type { PartnerCompanyOption } from '../types/partner-company.types';

type PartnerCompanySelectProps = {
  value?: PartnerCompanyOption | null;
  onChange: (company: PartnerCompanyOption | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: string;
};

export function PartnerCompanySelect({
  value,
  onChange,
  disabled = false,
  placeholder = 'Search partner companies...',
  label,
  error,
}: PartnerCompanySelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    searchQuery,
    setSearchQuery,
    options,
    isLoading,
    selectedCompany,
    selectCompany,
    syncSelectedCompany,
    clearSelection,
  } = usePartnerCompanySelect(value);

  // Sync external value with internal state (without clearing search query)
  useEffect(() => {
    if (value !== undefined) {
      syncSelectedCompany(value);
    }
  }, [value, syncSelectedCompany]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (company: PartnerCompanyOption) => {
    selectCompany(company);
    onChange(company);
    setIsOpen(false);
  };

  const handleClear = () => {
    clearSelection();
    onChange(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Selected value or search input */}
      <div
        className={`relative flex items-center border rounded-md transition-colors ${
          error ? 'border-red-500' : 'border-gray-300'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${
          isOpen ? 'ring-2 ring-[#026857] border-transparent' : ''
        }`}
      >
        {selectedCompany && !isOpen ? (
          // Display selected company
          <div className="flex items-center justify-between w-full px-3 py-2">
            <div className="flex items-center gap-2">
              {selectedCompany.logoUrl ? (
                <img
                  src={selectedCompany.logoUrl}
                  alt={`${selectedCompany.name} logo`}
                  className="h-6 w-6 rounded object-cover"
                />
              ) : (
                <Building2 className="h-5 w-5 text-gray-400" />
              )}
              <span className="text-gray-900">{selectedCompany.name}</span>
              <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                {selectedCompany.referralCode}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {!disabled && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Clear selection"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        ) : (
          // Search input
          <>
            <Search className="absolute left-3 h-4 w-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              disabled={disabled}
              placeholder={placeholder}
              className="w-full pl-10 pr-10 py-2 bg-transparent focus:outline-none disabled:cursor-not-allowed"
              aria-label="Search partner companies"
            />
            {isLoading && (
              <div className="absolute right-3">
                <div className="h-4 w-4 border-2 border-gray-300 border-t-[#026857] rounded-full animate-spin" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Error message */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="h-5 w-5 border-2 border-gray-300 border-t-[#026857] rounded-full animate-spin mx-auto mb-2" />
              Searching...
            </div>
          ) : options.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchQuery.length > 0 ? 'No companies found' : 'Type to search...'}
            </div>
          ) : (
            <ul role="listbox" aria-label="Partner companies">
              {options.map((company) => (
                <li
                  key={company.id}
                  role="option"
                  aria-selected={selectedCompany?.id === company.id}
                  onClick={() => handleSelect(company)}
                  className={`flex items-center justify-between px-3 py-2 cursor-pointer transition-colors ${
                    selectedCompany?.id === company.id
                      ? 'bg-[#026857]/10 text-[#026857]'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {company.logoUrl ? (
                      <img
                        src={company.logoUrl}
                        alt={`${company.name} logo`}
                        className="h-6 w-6 rounded object-cover"
                      />
                    ) : (
                      <Building2 className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{company.name}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {company.referralCode}
                      </div>
                    </div>
                  </div>
                  {selectedCompany?.id === company.id && (
                    <Check className="h-4 w-4 text-[#026857]" />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
